import { Box, Grid, Stack, Typography } from '@mui/material';
import { UserStatusChip } from 'components/UserStatusChip';
import { Trans, useTranslation } from 'react-i18next';
import { getDenseResponsiveFontSize } from 'theme/watchdutyTheme.utils';
import { useAuthState } from 'state/useAuthState';
import { DATE_ONLY_FORMAT, formatDate } from 'shared/dates';
import { PaymentMethodData } from 'shared/types';
import { BillingDetails } from './BillingDetails';

type SubscriptionDetailsProps = {
  isIosSubscription: boolean;
  isMonthlySubscription: boolean;
  subscriptionPrice: number;
  isRecurringSubscription: boolean;
  dateActiveUntil?: string;
  savedPaymentMethod?: PaymentMethodData;
};

const termStyles = {
  textTransform: 'uppercase',
  fontSize: getDenseResponsiveFontSize('0.8rem'),
} as const;

export const SubscriptionDetails = (
  props: SubscriptionDetailsProps,
): JSX.Element => {
  const {
    isIosSubscription,
    isMonthlySubscription,
    subscriptionPrice,
    isRecurringSubscription,
    dateActiveUntil,
    savedPaymentMethod,
  } = props;
  const { t } = useTranslation();
  const { isProMember, isOrganizationUser } = useAuthState();

  return (
    <Grid container spacing={1.5}>
      <Grid size={3}>
        <Typography variant="subtitle1" color="textSecondary" sx={termStyles}>
          {t('membership.subscription.plan')}
        </Typography>
      </Grid>

      <Grid size={9}>
        <Stack direction="row" spacing={1} alignItems="center">
          <UserStatusChip type={isProMember ? 'pro' : 'members'} />

          <Typography variant="body2" component="span">
            {isOrganizationUser
              ? t('organization.featureName')
              : t(
                  isMonthlySubscription
                    ? 'membership.subscription.monthlyPrice'
                    : 'membership.subscription.yearlyPrice',
                  {
                    price: subscriptionPrice,
                  },
                )}
          </Typography>
        </Stack>
      </Grid>

      <Grid size={3}>
        <Typography variant="subtitle1" color="textSecondary" sx={termStyles}>
          {t('membership.subscription.status')}
        </Typography>
      </Grid>

      <Grid size={9}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 8,
                height: 8,
                bgcolor: isRecurringSubscription
                  ? 'success.main'
                  : 'warning.main',
                borderRadius: '50%',
              }}
              data-testid={
                isRecurringSubscription
                  ? 'success-indicator'
                  : 'warning-indicator'
              }
            />

            {Boolean(dateActiveUntil) && (
              <Typography variant="body2" component="span">
                <Trans
                  i18nKey="membership.subscription.activeUntil"
                  values={{
                    date: formatDate(
                      new Date(dateActiveUntil!),
                      DATE_ONLY_FORMAT,
                    ),
                  }}
                >
                  <b>Active</b> until Dec 19, 2026
                </Trans>
              </Typography>
            )}
          </Stack>
        </Stack>
      </Grid>

      {isRecurringSubscription && (
        <>
          <Grid size={3}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              sx={termStyles}
            >
              {t('membership.subscription.billing')}
            </Typography>
          </Grid>

          <Grid size={9}>
            <BillingDetails
              isIosSubscription={isIosSubscription}
              paymentMethod={savedPaymentMethod}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};
